'use client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { LOGO_SRC_DARK_BACKGROUND } from '@/components/logo/index';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { ArrowLeft01Icon, Cancel01Icon } from '@/components/ui/icon/glyphs';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { Link } from '@/i18n/navigation';
import type { useFundingChrome } from './funding-chrome';

export function FundingDialog({
  open,
  onClose,
  onHistory,
  chrome,
  children,
  history = false,
  direct = false,
}: {
  open: boolean;
  history?: boolean;
  direct?: boolean;
  onClose(): void;
  onHistory(): void;
  chrome: ReturnType<typeof useFundingChrome>;
  children: ReactNode;
}): ReactNode {
  const t = useTranslations('onramp');
  const controls = useTranslations('filters');
  return (
    <SilkSheet
      open={open}
      onOpenChange={(open: boolean): void => {
        if (!open) onClose();
      }}
      title={chrome.title}
      header={
        <div className="flex min-h-7 items-center gap-3 pb-4">
          {chrome.hasBack &&
            (chrome.backHref ? (
              <AnimatedButton variant="tertiary" size="sm" className="h-8.25 w-7 p-1" asChild>
                <Link href={chrome.backHref} onClick={chrome.back} aria-label={t('back')}>
                  <Icon icon={ArrowLeft01Icon} className="size-4.5" />
                </Link>
              </AnimatedButton>
            ) : (
              <AnimatedButton
                type="button"
                variant="tertiary"
                size="sm"
                className="h-8.25 w-7 p-1"
                aria-label={t('back')}
                prefix={ArrowLeft01Icon}
                prefixClassName="size-4.5"
                onClick={chrome.back}
              />
            ))}
          <p aria-hidden="true" className="flex-1 text-heading-md">
            {chrome.title}
          </p>
          <AnimatedButton
            type="button"
            variant="tertiary"
            size="sm"
            className="h-8.25 w-7 p-1"
            aria-label={controls('close')}
            prefix={Cancel01Icon}
            prefixClassName="size-4.5"
            onClick={onClose}
          />
        </div>
      }
      closeButton={false}
      disableDesktopSwipe
      variant="detached"
      size="md"
      contentClassName="pt-0 sm:pt-0"
      footerClassName="border-t-[1px]"
      sheetClassName={history ? 'sm:max-w-170' : 'sm:max-w-md'}
      footer={
        <div className="flex flex-col items-center gap-3 py-2">
          {chrome.picker && (
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                className="cursor-pointer text-xs text-foreground-secondary underline-offset-4 hover:underline focus-visible:underline"
                onClick={onHistory}
              >
                {t('view_history')}
              </button>
              {chrome.hasProfile && (
                <>
                  <span aria-hidden="true" className="text-xs text-foreground-secondary">
                    ·
                  </span>
                  <button
                    type="button"
                    className="cursor-pointer text-xs text-foreground-secondary underline-offset-4 hover:underline focus-visible:underline"
                    onClick={chrome.openProfile}
                  >
                    {t('view_kyc_information')}
                  </button>
                </>
              )}
            </div>
          )}
          <div
            className={`flex items-center justify-center ${chrome.picker ? 'gap-3' : 'gap-1.25'}`}
          >
            {direct ? (
              <BrandImage src={LOGO_SRC_DARK_BACKGROUND} alt="Morfi" direct />
            ) : (
              <>
                {chrome.picker ? null : <span className="text-micro">{t('powered_by')}</span>}
                <BrandImage
                  src="/assets/onramp/moonpay.svg"
                  alt="MoonPay"
                  partnership={chrome.picker}
                />
                {chrome.picker && (
                  <>
                    <span aria-hidden="true" className="text-caption text-foreground-secondary">
                      ×
                    </span>
                    <BrandImage src={LOGO_SRC_DARK_BACKGROUND} alt="Morfi" />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      }
    >
      {children}
    </SilkSheet>
  );
}
function BrandImage({
  src,
  alt,
  partnership = false,
  direct = false,
}: {
  src: string;
  alt: string;
  partnership?: boolean;
  direct?: boolean;
}): ReactNode {
  return (
    <OptimizedImage
      // ALLOW-NSFW-FALSE: bundled, approved payment and brand artwork.
      nsfw={false}
      variant="thumbnail"
      // ALLOW-NO-PREVIEW: decorative payment icons and wordmarks are not previewable media.
      // ALLOW-NO-PREVIEW: Bundled payment/status artwork is decorative, not expandable user media.
      preview={false}
      loadReveal="none"
      rounded="rounded-item"
      imageClassName="object-contain invert dark:invert-0"
      src={src}
      alt={alt}
      size={
        direct
          ? 'h-4.5 w-15'
          : alt === 'Morfi'
            ? 'h-6 w-16.75'
            : partnership
              ? 'h-6 w-21.25'
              : 'h-6 w-20.5'
      }
      className="bg-transparent"
    />
  );
}
